/* =====================================================================
   Pet Releaf – ADA / WCAG 2.1 AA behaviour fixes (Sep 2026 audit)

   Loaded with `defer` from layout/theme.liquid. No dependencies.
   Everything is defensive: if a selector isn't on the page, that part
   simply does nothing. Third-party UI (AMP cart, Klaviyo, SavedBy) is
   patched from the outside; those vendors should still be asked to fix
   their own markup (see the tracker).

   Exposes window.PRA11y = { announce, trapFocus, releaseFocus } for
   section scripts (custom-product-grid, collection variant selectors).
   ===================================================================== */
(function () {
  'use strict';

  if (window.PRA11y && window.PRA11y.__loaded) return;

  var FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', 'iframe', 'object', 'embed',
    '[contenteditable="true"]', '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
    var rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return false;
    // translated fully off-screen counts as hidden
    if (rect.right <= 0 || rect.left >= window.innerWidth || rect.bottom <= 0 || rect.top >= window.innerHeight + 2000) return false;
    return true;
  }

  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return !el.closest('[inert]') && el.getAttribute('aria-hidden') !== 'true' && isVisible(el);
    });
  }

  /* ------------------------------------------------------------------
     Live-region announcements (WCAG 4.1.3)
     ------------------------------------------------------------------ */
  var announceTimer;
  function announce(message) {
    var region = document.getElementById('a11y-announcer');
    if (!region) {
      region = document.createElement('div');
      region.id = 'a11y-announcer';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }
    clearTimeout(announceTimer);
    region.textContent = '';
    announceTimer = setTimeout(function () { region.textContent = message; }, 120);
  }

  /* ------------------------------------------------------------------
     Modal behaviour: focus in, Tab trapped, background inert,
     Escape closes, focus returns to the trigger.
     (ADA 005, 013, 031, 035, 105, 101, Collections ADA-031, Cart ADA 002/003/034)
     ------------------------------------------------------------------ */
  var active = []; // stack of { el, keep, returnTo, onEscape, inerted }

  function makeBackgroundInert(keepEls) {
    var inerted = [];
    function walk(node) {
      if (!node || node === document.body || !node.parentElement) return;
      var parent = node.parentElement;
      Array.prototype.forEach.call(parent.children, function (sib) {
        if (sib === node) return;
        if (/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(sib.tagName)) return;
        if (sib.id === 'a11y-announcer' || sib.id === 'a11y-cart-announcer') return;
        // don't hide anything that contains an element we must keep usable (e.g. the menu toggle)
        var containsKeep = keepEls.some(function (k) { return k && k !== node && sib.contains(k); });
        if (containsKeep) return;
        if (!sib.hasAttribute('inert')) {
          sib.setAttribute('inert', '');
          sib.setAttribute('data-pr-inert', '');
          inerted.push(sib);
        }
      });
      walk(parent);
    }
    keepEls.forEach(function (k) { if (k) walk(k); });
    return inerted;
  }

  function restoreBackground(inerted) {
    (inerted || []).forEach(function (el) {
      if (el.hasAttribute('data-pr-inert')) {
        el.removeAttribute('inert');
        el.removeAttribute('data-pr-inert');
      }
    });
  }

  function onTrapKeydown(e) {
    var top = active[active.length - 1];
    if (!top) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      if (top.onEscape) { e.preventDefault(); top.onEscape(); }
      return;
    }
    if (e.key !== 'Tab') return;
    var scope = [top.el].concat(top.keep || []);
    var items = [];
    scope.forEach(function (s) { if (s) items = items.concat(s.matches && s.matches(FOCUSABLE) && isVisible(s) ? [s] : [], focusables(s)); });
    if (!items.length) { e.preventDefault(); top.el.focus(); return; }
    var first = items[0], last = items[items.length - 1];
    var inScope = scope.some(function (s) { return s && s.contains(document.activeElement); });
    if (e.shiftKey && (document.activeElement === first || !inScope)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || !inScope)) { e.preventDefault(); first.focus(); }
  }
  document.addEventListener('keydown', onTrapKeydown, true);

  function trapFocus(el, opts) {
    if (!el) return;
    opts = opts || {};
    if (active.some(function (a) { return a.el === el; })) return;
    el.setAttribute('data-pr-managed', '');
    var entry = {
      el: el,
      keep: opts.keep || [],
      returnTo: opts.returnTo || document.activeElement,
      onEscape: opts.onEscape || null
    };
    entry.inerted = makeBackgroundInert([el].concat(entry.keep));
    active.push(entry);
    if (opts.focus !== false) {
      setTimeout(function () {
        if (el.contains(document.activeElement)) return;
        var target = opts.initialFocus || el.querySelector('[data-a11y-initial-focus]') || focusables(el)[0];
        if (!target) { if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1'); target = el; }
        try { target.focus({ preventScroll: false }); } catch (err) { target.focus(); }
      }, 60);
    }
  }

  function releaseFocus(el, opts) {
    opts = opts || {};
    for (var i = active.length - 1; i >= 0; i--) {
      if (active[i].el === el) {
        var entry = active.splice(i, 1)[0];
        restoreBackground(entry.inerted);
        el.removeAttribute('data-pr-managed');
        if (opts.returnFocus !== false && entry.returnTo && entry.returnTo.focus && document.contains(entry.returnTo)) {
          setTimeout(function () { entry.returnTo.focus(); }, 30);
        }
        return;
      }
    }
  }

  window.PRA11y = { announce: announce, trapFocus: trapFocus, releaseFocus: releaseFocus, __loaded: true };

  /* Small helper: watch an element and call back when it opens/closes */
  function watchOpenState(getEl, isOpenFn, onChange, extraTargets) {
    var last = null;
    function check() {
      var el = getEl();
      if (!el) return;
      var open = !!isOpenFn(el);
      if (open !== last) { last = open; onChange(open, el); }
    }
    var mo = new MutationObserver(function () { window.requestAnimationFrame(check); });
    function observe() {
      var el = getEl();
      var targets = [document.documentElement, document.body].concat(el ? [el] : []).concat((extraTargets && extraTargets()) || []);
      targets.forEach(function (t) {
        if (t) mo.observe(t, { attributes: true, attributeFilter: ['class', 'style', 'aria-hidden', 'hidden', 'open'] });
      });
    }
    observe();
    // transitions can finish after the class change
    document.addEventListener('transitionend', check, true);
    window.addEventListener('resize', check);
    check();
    return check;
  }

  function isSmallScreen() { return window.matchMedia('(max-width: 768px)').matches; }

  /* ==================================================================
     1. Mobile navigation (ADA 004, 005, 105)
     ================================================================== */
  function initMobileNav() {
    var trigger = document.querySelector('.mobile-nav-trigger');
    var nav = document.getElementById('MobileNav');
    if (!trigger || !nav) return;

    nav.setAttribute('role', 'dialog');
    nav.setAttribute('aria-modal', 'true');
    nav.setAttribute('aria-label', 'Main menu');

    watchOpenState(
      function () { return nav; },
      function (el) {
        if (!isSmallScreen()) return false;
        return el.classList.contains('is-active') || el.classList.contains('is-open') || isVisible(el);
      },
      function (open) {
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        trigger.setAttribute('aria-label', open ? 'Close menu' : 'Menu');
        if (open) {
          nav.removeAttribute('aria-hidden');
          trapFocus(nav, {
            keep: [trigger],
            returnTo: trigger,
            onEscape: function () { trigger.click(); }
          });
        } else {
          releaseFocus(nav);
        }
      }
    );
  }

  /* Sub-menus inside the mobile nav: keep aria-expanded in sync with the theme's is-open class */
  function initSubmenuState() {
    var mo = new MutationObserver(function (records) {
      records.forEach(function (r) {
        var el = r.target;
        if (el.matches && el.matches('.slide-nav__dropdown')) {
          var btn = document.querySelector('[aria-controls="' + el.id + '"]');
          if (btn) btn.setAttribute('aria-expanded', el.classList.contains('is-active') || el.classList.contains('is-open') ? 'true' : 'false');
        }
      });
    });
    document.querySelectorAll('.slide-nav__dropdown[id]').forEach(function (el) {
      mo.observe(el, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* ==================================================================
     2. Search overlay (ADA-101)
     ================================================================== */
  function initSearch() {
    var container = document.querySelector('.site-header__search-container');
    var trigger = document.querySelector('.js-search-header');
    if (!container) return;
    container.setAttribute('role', 'dialog');
    container.setAttribute('aria-label', 'Search');
    watchOpenState(
      function () { return container; },
      function (el) { return el.classList.contains('is-active') || isVisible(el); },
      function (open) {
        if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          container.setAttribute('aria-modal', 'true');
          var input = container.querySelector('input[type="search"]');
          var cancel = container.querySelector('.site-header__search-btn--cancel');
          trapFocus(container, {
            returnTo: trigger,
            initialFocus: input,
            onEscape: function () { if (cancel) cancel.click(); }
          });
          if (cancel && !cancel.getAttribute('aria-label')) cancel.setAttribute('aria-label', 'Close search');
        } else {
          container.removeAttribute('aria-modal');
          releaseFocus(container);
        }
      }
    );
  }

  /* ==================================================================
     3. Collection filters (ADA 027, ADA-031, ADA-033 Collections, ADA-102)
     ================================================================== */
  function initFilters() {
    var toggle = document.querySelector('[data-a11y-filter-toggle]');
    var sidebar = document.getElementById('CollectionSidebar');
    if (!sidebar) return;

    function panel() {
      // On mobile the theme moves the filter wrapper into #CollectionInlineFilterWrap
      return document.querySelector('#CollectionInlineFilterWrap .filter-wrapper') ||
             document.querySelector('#CollectionSidebarFilterWrap') ||
             sidebar.querySelector('.filter-wrapper');
    }

    watchOpenState(
      panel,
      function (el) {
        if (!isSmallScreen()) return false;
        var close = el.querySelector('.mobile-filter-close');
        return isVisible(el) && (!close || isVisible(close));
      },
      function (open, el) {
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (!isSmallScreen()) {
          el.removeAttribute('inert');
          el.removeAttribute('role'); el.removeAttribute('aria-modal');
          return;
        }
        if (open) {
          el.removeAttribute('inert');
          el.setAttribute('role', 'dialog');
          el.setAttribute('aria-modal', 'true');
          el.setAttribute('aria-label', 'Filter products');
          var closeBtn = el.querySelector('.mobile-filter-close');
          trapFocus(el, {
            returnTo: toggle,
            initialFocus: closeBtn,
            onEscape: function () { if (closeBtn) closeBtn.click(); else if (toggle) toggle.click(); }
          });
        } else {
          releaseFocus(el);
          el.removeAttribute('role'); el.removeAttribute('aria-modal');
          // ADA-102: a collapsed off-canvas panel must not be reachable by screen readers
          el.setAttribute('inert', '');
        }
      },
      function () { return [document.getElementById('CollectionInlineFilterWrap'), sidebar]; }
    );

    // aria-expanded on collapsible filter group headers (theme toggles .is-open)
    function syncExpanded(btn) {
      btn.setAttribute('aria-expanded', btn.classList.contains('is-open') ? 'true' : 'false');
    }
    var mo = new MutationObserver(function (records) {
      records.forEach(function (r) { if (r.target.hasAttribute('data-a11y-sync-expanded')) syncExpanded(r.target); });
    });
    function bind() {
      document.querySelectorAll('[data-a11y-sync-expanded]').forEach(function (btn) {
        syncExpanded(btn);
        mo.observe(btn, { attributes: true, attributeFilter: ['class'] });
      });
    }
    bind();
    document.addEventListener('collection:reloaded', bind);

    // Announce the result count after filtering / sorting re-renders the grid
    document.addEventListener('collection:reloaded', function () {
      var items = document.querySelectorAll('#CollectionAjaxContent .grid-item.grid-product, .collection-grid .grid-product');
      var n = items.length;
      announce(n + (n === 1 ? ' product' : ' products') + ' shown');
    });
  }

  /* ==================================================================
     4. Cart link label + count (ADA 011, 012)
     ================================================================== */
  function setCartLabel(count) {
    if (typeof count !== 'number' || isNaN(count)) return;
    document.querySelectorAll('[data-a11y-cart-link]').forEach(function (link) {
      link.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items') + ', opens cart panel');
    });
  }
  function initCartLink() {
    document.addEventListener('cart:updated', function (e) {
      var cart = e.detail && e.detail.cart;
      if (cart && typeof cart.item_count === 'number') setCartLabel(cart.item_count);
    });
    document.addEventListener('cart:ui:updated', function (e) {
      if (e.detail && typeof e.detail.count === 'number') setCartLabel(e.detail.count);
    });
    var bubble = document.querySelector('.cart-link__bubble-num');
    if (bubble) {
      new MutationObserver(function () {
        setCartLabel(parseInt(bubble.textContent.trim(), 10) || 0);
      }).observe(bubble, { childList: true, characterData: true, subtree: true });
    }
  }

  /* ==================================================================
     5. AMP cart drawer & any other aria-modal dialog from apps
        (Cart ADA-034/035, 036, 038, 040, 042, 044; Site-wide ADA-099)
     ================================================================== */
  function labelIconButtons(root, productNameFor) {
    root.querySelectorAll('button, [role="button"], a').forEach(function (btn) {
      if (btn.getAttribute('aria-label') || btn.getAttribute('aria-labelledby')) return;
      var text = (btn.textContent || '').replace(/\s+/g, ' ').trim();
      var cls = (btn.className && btn.className.baseVal !== undefined ? btn.className.baseVal : btn.className) || '';
      cls = String(cls).toLowerCase();
      var name = productNameFor ? productNameFor(btn) : '';
      var suffix = name ? ' ' + name : '';
      if (/^add( to cart)?$/i.test(text)) {
        // Cart ADA 002: "ADD" (all caps) is spelled out letter by letter and has no product context
        var card = btn.closest('[class*="recommend"], [class*="upsell"], [class*="product"], li');
        var t = card && card.querySelector('[class*="title"], [class*="name"]');
        var pn = (t && t.textContent.replace(/\s+/g, ' ').trim()) || name;
        btn.setAttribute('aria-label', 'Add' + (pn ? ' ' + pn : '') + ' to cart');
      } else if (/close/.test(cls) || text === '×' || text === '✕' || text === 'x' || text === 'X') {
        btn.setAttribute('aria-label', /cart/.test(cls) || root.matches('.amp-cart-drawer, #amp-theme-extension *') ? 'Close cart' : 'Close');
      } else if (/remove|delete|trash/.test(cls)) {
        btn.setAttribute('aria-label', 'Remove' + suffix + ' from cart');
      } else if (text === '+' || /plus|increase|increment/.test(cls)) {
        btn.setAttribute('aria-label', 'Increase quantity' + (name ? ' of ' + name : ''));
      } else if (text === '-' || text === '−' || text === '–' || /minus|decrease|decrement/.test(cls)) {
        btn.setAttribute('aria-label', 'Decrease quantity' + (name ? ' of ' + name : ''));
      } else if (!text && btn.querySelector('svg, img')) {
        var img = btn.querySelector('img[alt]');
        if (img && img.alt) btn.setAttribute('aria-label', img.alt);
      }
    });
  }

  function makeClickableKeyboardAccessible(root) {
    // Elements with click handlers that aren't real buttons (e.g. AMP "remove" icons) – ADA-040
    root.querySelectorAll('[class*="remove"], [class*="delete"], [class*="tooltip"], [class*="info-icon"]').forEach(function (el) {
      if (el.matches(FOCUSABLE) || el.closest('button, a[href]')) return;
      if (el.querySelector('button, a[href], input, select')) return;
      el.setAttribute('tabindex', '0');
      if (!el.getAttribute('role')) el.setAttribute('role', 'button');
      if (!el.dataset.prKeyBound) {
        el.dataset.prKeyBound = '1';
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.click(); }
        });
      }
    });
  }

  function lineItemName(el) {
    var line = el.closest('[class*="cart-line"], [class*="line-item"], [class*="cart__item"], li');
    if (!line) return '';
    var t = line.querySelector('[class*="title"], [class*="name"], a[href*="/products/"]');
    return t ? t.textContent.replace(/\s+/g, ' ').trim().slice(0, 80) : '';
  }

  function enhanceCartDrawer(drawer) {
    if (!drawer) return;
    if (!drawer.getAttribute('role')) drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');

    // Heading / count (ADA-036 / Cart ADA-035)
    var count = null;
    var bubble = document.querySelector('.cart-link__bubble-num');
    if (bubble) count = parseInt(bubble.textContent.trim(), 10);
    var label = 'Shopping cart' + (typeof count === 'number' && !isNaN(count) ? ', ' + count + (count === 1 ? ' item' : ' items') : '');
    if (!drawer.getAttribute('aria-labelledby')) drawer.setAttribute('aria-label', label);

    labelIconButtons(drawer, lineItemName);

    // Cart ADA 001 / ADA-001: product images with no alt. If the product name is already
    // in the same link/line, the image is decorative (alt=""), otherwise use the product name.
    drawer.querySelectorAll('img:not([alt]), img[alt="image"], img[alt="product image"]').forEach(function (img) {
      var n = lineItemName(img);
      var link = img.closest('a');
      var linkHasText = link && (link.textContent || '').trim().length > 0;
      img.setAttribute('alt', linkHasText ? '' : (n || ''));
    });
    makeClickableKeyboardAccessible(drawer);

    // Selects without a name (subscription frequency in the cart) – ADA-039 / 041
    drawer.querySelectorAll('select').forEach(function (sel) {
      if (sel.getAttribute('aria-label') || (sel.id && drawer.querySelector('label[for="' + sel.id + '"]'))) return;
      var n = lineItemName(sel);
      sel.setAttribute('aria-label', 'Delivery frequency' + (n ? ' for ' + n : ''));
    });

    // Recommended products: images inside links should name the product – ADA-041/043 (cart)
    drawer.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      if ((a.textContent || '').trim() || a.getAttribute('aria-label')) return;
      var img = a.querySelector('img');
      var card = a.closest('[class*="recommend"], [class*="upsell"], [class*="product"]');
      var title = card && card.querySelector('[class*="title"], [class*="name"]');
      var name = (img && img.alt) || (title && title.textContent.trim());
      if (name) a.setAttribute('aria-label', name.replace(/\s+/g, ' ').trim());
    });

    // Checkout button: include the total so it's heard without hunting for it – ADA-042/044/045
    var checkout = drawer.querySelector('.amp-cart__footer-checkout-button, [name="checkout"], button[class*="checkout"]');
    if (checkout) {
      var totalEl = drawer.querySelector('[class*="subtotal"] [class*="price"], [class*="total"] [class*="price"], [class*="subtotal-price"], [class*="total-price"]');
      var total = totalEl ? totalEl.textContent.replace(/\s+/g, ' ').trim() : '';
      var text = checkout.textContent.replace(/\s+/g, ' ').trim() || 'Checkout';
      if (total && text.indexOf(total) === -1) checkout.setAttribute('aria-label', text + ', order total ' + total);
    }

    // Free shipping / free gift progress bar – ADA-037/039
    drawer.querySelectorAll('[class*="progress"], [class*="shipping-bar"], [class*="tier"]').forEach(function (bar) {
      if (bar.getAttribute('role') || bar.querySelector('[role="progressbar"]')) return;
      var txt = (bar.textContent || '').replace(/\s+/g, ' ').trim();
      if (!txt) return;
      bar.setAttribute('role', 'status');
      bar.setAttribute('aria-live', 'polite');
    });
  }

  function findCartDrawer() {
    return document.querySelector('.amp-cart-drawer') ||
           document.querySelector('#amp-theme-extension [role="dialog"]') ||
           document.querySelector('#amp-theme-extension');
  }

  function initAppDialogs() {
    var cartTrigger = document.getElementById('HeaderCartTrigger');

    // Generic watcher: any app dialog that declares aria-modal but doesn't move focus.
    var seen = new WeakMap();
    function scan() {
      var dialogs = document.querySelectorAll('[aria-modal="true"], [role="dialog"], .amp-cart-drawer');
      Array.prototype.forEach.call(dialogs, function (d) {
        if (d.id === 'MobileNav' || d.closest('.site-header__search-container') || d.closest('#CollectionSidebar, #CollectionInlineFilterWrap')) return;
        if (d.hasAttribute('data-pr-managed') && !seen.get(d)) return; // managed by a section script
        var isCart = d.matches('.amp-cart-drawer') || !!d.closest('#amp-theme-extension');
        var open = isVisible(d) && d.getAttribute('aria-hidden') !== 'true';
        var was = seen.get(d) || false;
        if (open === was) { if (open && isCart) enhanceCartDrawer(d); return; }
        seen.set(d, open);
        if (open) {
          if (isCart) enhanceCartDrawer(d);
          else labelIconButtons(d);
          var closeBtn = d.querySelector('[aria-label*="lose" i], [class*="close"], button[title*="lose" i]');
          trapFocus(d, {
            returnTo: isCart ? (cartTrigger || document.activeElement) : document.activeElement,
            onEscape: function () {
              if (closeBtn) closeBtn.click();
              else if (isCart && window.AMP && typeof window.AMP.closeCart === 'function') window.AMP.closeCart();
            }
          });
        } else {
          releaseFocus(d);
        }
      });
    }
    // Throttled: carousels mutate styles every frame, so ignore those and scan at most every 200ms
    var pending = false;
    var IGNORE = '.swiper-wrapper, .swiper-slide, .flickity-slider, .flickity-viewport, [data-pr-inert]';
    var mo = new MutationObserver(function (records) {
      if (pending) return;
      var relevant = records.some(function (r) {
        var t = r.target;
        return !(t.nodeType === 1 && t.closest && t.closest(IGNORE));
      });
      if (!relevant) return;
      pending = true;
      setTimeout(function () { pending = false; scan(); }, 200);
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'aria-hidden', 'open'] });
    document.addEventListener('drawerOpen', function () { setTimeout(scan, 80); });
    scan();
  }

  /* ==================================================================
     6. Product gallery announcements (ADA-047)
     ================================================================== */
  function initGallery() {
    document.addEventListener('click', function (e) {
      var thumb = e.target.closest && e.target.closest('[data-product-thumb]');
      if (!thumb) return;
      var label = thumb.getAttribute('aria-label') || '';
      if (label) announce(label.replace(/^Show /, 'Showing '));
      document.querySelectorAll('[data-product-thumb]').forEach(function (t) {
        t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
      });
    });
  }

  /* ==================================================================
     7. Decorative background media (ADA-063)
     ================================================================== */
  function initDecorativeMedia() {
    function apply() {
      document.querySelectorAll('[data-a11y-decorative-media] iframe, [data-a11y-decorative-media] video').forEach(function (m) {
        m.setAttribute('tabindex', '-1');
        m.setAttribute('aria-hidden', 'true');
        if (m.tagName === 'IFRAME' && !m.title) m.title = 'Decorative background video';
      });
    }
    apply();
    var holders = document.querySelectorAll('[data-a11y-decorative-media]');
    if (holders.length) {
      var mo = new MutationObserver(apply);
      holders.forEach(function (h) { mo.observe(h, { childList: true, subtree: true }); });
    }
  }

  /* ==================================================================
     8. Form messages that should receive focus (ADA 027 / 032)
     ================================================================== */
  function initAutofocusMessages() {
    var msg = document.querySelector('[data-a11y-autofocus]');
    if (msg) setTimeout(function () { msg.focus(); }, 300);
  }

  /* ==================================================================
     9. Iframes without a title (general 4.1.2 hygiene for app embeds)
     ================================================================== */
  function initIframeTitles() {
    function apply() {
      document.querySelectorAll('iframe:not([title])').forEach(function (f) {
        var src = f.getAttribute('src') || '';
        if (/youtube|vimeo/.test(src)) f.title = 'Video';
        else if (/revenuehunt/.test(src)) f.title = 'Product finder quiz';
        else if (/klaviyo/.test(src)) f.title = 'Sign up form';
      });
    }
    apply();
    setTimeout(apply, 3000);
  }

  /* ==================================================================
     10. Collection quick-add modal (ADA 013 / 015 on collection cards)
         The modal already has role="dialog"; make it modal so the
         generic dialog watcher traps focus and hides the background.
     ================================================================== */
  function initPlpModal() {
    var m = document.getElementById('PlpAtcModal');
    if (!m) return;
    m.setAttribute('aria-modal', 'true');
    var close = m.querySelector('.js-plp-modal-close');
    if (close) close.setAttribute('aria-label', 'Close product options');
  }

  function init() {
    try { initPlpModal(); } catch (e) { console.warn('[a11y] plp modal', e); }
    try { initMobileNav(); } catch (e) { console.warn('[a11y] mobile nav', e); }
    try { initSubmenuState(); } catch (e) { console.warn('[a11y] submenus', e); }
    try { initSearch(); } catch (e) { console.warn('[a11y] search', e); }
    try { initFilters(); } catch (e) { console.warn('[a11y] filters', e); }
    try { initCartLink(); } catch (e) { console.warn('[a11y] cart link', e); }
    try { initAppDialogs(); } catch (e) { console.warn('[a11y] app dialogs', e); }
    try { initGallery(); } catch (e) { console.warn('[a11y] gallery', e); }
    try { initDecorativeMedia(); } catch (e) { console.warn('[a11y] media', e); }
    try { initAutofocusMessages(); } catch (e) { console.warn('[a11y] messages', e); }
    try { initIframeTitles(); } catch (e) { console.warn('[a11y] iframes', e); }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();